import { useCallback, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter, usePathname } from '@/i18n/navigation'
import { saveBranchData } from '../_store/common/common.slice'
import { IRedux } from '../_store/types'
import { IBranch } from '../_types'

interface IData {
  id: IBranch['id']
  menu_id: IBranch['menu_id']
  address: IBranch['address']
  name: IBranch['name']
  location: IBranch['location']
  slug: IBranch['slug']
  future_delivery_order_time: IBranch['future_delivery_order_time']
  future_self_pickup_order_time: IBranch['future_self_pickup_order_time']
  delivery_time?: IBranch['delivery_time']
  work_hour_start: IBranch['work_hour_start']
  work_hour_end: IBranch['work_hour_end']
  is_work_24_7?: IBranch['is_work_24_7']
}

/**
 * Custom hook that wraps saveBranchData and automatically refreshes
 * the router when menu_id changes, but ONLY on the home page.
 * This ensures server components refetch data without affecting UX on other pages.
 *
 * Uses a ref to track the current menu_id to prevent infinite refresh loops
 * while keeping the callback stable for use in dependency arrays.
 *
 * Includes safeguards to prevent infinite loops:
 * - Tracks if a refresh is already in progress
 * - Compares menu_id values before refreshing
 * - Updates ref synchronously to prevent race conditions
 */
export function useSaveBranchData() {
  const dispatch = useDispatch()
  const router = useRouter()
  const pathname = usePathname()

  // Track current menu_id in Redux state
  const currentMenuId = useSelector(
    (state: IRedux) => state.common.branch?.menu_id
  )
  // Use ref to track menu_id without causing callback recreation
  const menuIdRef = useRef<string | undefined>(currentMenuId)
  // Track if a refresh is already in progress to prevent infinite loops
  const isRefreshingRef = useRef(false)

  // Update ref when Redux state changes (but only if not currently refreshing)
  // This prevents race conditions where refresh triggers state updates
  useEffect(() => {
    if (!isRefreshingRef.current) {
      menuIdRef.current = currentMenuId
    }
  }, [currentMenuId])

  // Check if we're on the home page (root path)
  // Home page pathname is '/' or '/{locale}' (e.g., '/en', '/ru', '/uz', '/kk')
  // We check if pathname has at most 2 segments (empty string + locale, or just '/')
  const pathSegments = pathname.split('/').filter(Boolean)
  const isHomePage =
    pathname === '/' ||
    pathSegments.length === 1 ||
    pathname.includes('/landing-menu')

  // Stable callback that doesn't recreate when menu_id changes
  const saveBranch = useCallback(
    (branchData: IData) => {
      const previousMenuId = menuIdRef.current
      const newMenuId = branchData?.menu_id
      const menuIdChanged = previousMenuId !== newMenuId

      // Always dispatch to update Redux state and cookies
      // This ensures branch data is saved even if menu_id hasn't changed
      dispatch(saveBranchData(branchData))

      // Update ref immediately for next comparison
      menuIdRef.current = newMenuId

      // Only refresh on home page when menu_id actually changed
      // This prevents unnecessary re-renders and potential infinite loops
      if (isHomePage && menuIdChanged) {
        // Prevent concurrent refreshes to avoid infinite loops
        // This guards against rapid successive calls or refresh-triggered re-renders
        if (isRefreshingRef.current) {
          return
        }

        // Set flag to prevent concurrent refreshes
        isRefreshingRef.current = true
        
        // Use requestAnimationFrame to ensure refresh happens after state update
        // This helps prevent race conditions with Redux state updates
        requestAnimationFrame(() => {
          router.refresh()
          
          // Reset flag after refresh completes (Next.js refresh is async)
          // Use a timeout to ensure we don't block subsequent legitimate calls
          // The delay allows the refresh to complete before allowing another refresh
          setTimeout(() => {
            isRefreshingRef.current = false
          }, 100)
        })
      }
    },
    [dispatch, router, isHomePage]
  )

  return saveBranch
}
